import React from 'react';
import { cn } from '../../utils/helpers';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', children, className, type = 'button', ...props }, ref) => {
    const baseStyles =
      'inline-flex items-center justify-center gap-2 font-semibold rounded-lg border transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5E2A6F]/30 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 box-border whitespace-nowrap';

    const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
      primary:
        'border-transparent bg-[#F98C10] text-white shadow-sm hover:bg-[#E57D06] active:bg-[#D97305]',
      secondary:
        'border-transparent bg-[#5E2A6F] text-white shadow-sm hover:bg-[#4d2259] active:bg-[#431e4d]',
      outline:
        'border-[#E0DCE5] bg-white text-[#261D30] shadow-sm hover:bg-[#FAFAFA] active:bg-[#F0EEF2]',
      ghost:
        'border-transparent bg-transparent text-[#261D30] shadow-none hover:bg-neutral-100 active:bg-neutral-200',
      danger:
        'border-transparent bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-800',
    };

    const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
      sm: 'h-8 min-h-[32px] px-3 text-sm [&_svg]:h-4 [&_svg]:w-4',
      md: 'h-10 min-h-[40px] px-4 text-sm [&_svg]:h-4 [&_svg]:w-4',
      lg: 'h-12 min-h-[48px] px-6 text-base [&_svg]:h-5 [&_svg]:w-5',
    };

    return (
      <button
        ref={ref}
        type={type}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
